/** Shared rendering helpers (no framework — template strings + innerHTML). */
import { teams } from "../app/data.ts";
import { t as tr, lang } from "../app/i18n.ts"; // `t` est déjà un nom de variable local (équipe)

const byCode = new Map(teams.map((t) => [t.id, t]));

/** Suffixe ordinal d'un rang, selon la langue (1ᵉʳ/2ᵉ · 1st/2nd/3rd/4th · 1.º).
 *  Rendu en exposant à côté d'un classement (eloRank, place de groupe…). */
export function ordinalSuffix(n: number): string {
  const l = lang();
  if (l === "fr") return n === 1 ? "er" : "e";
  if (l === "es") return "º";
  const t = n % 100; // anglais : 11–13 → th, sinon dernier chiffre
  if (t >= 11 && t <= 13) return "th";
  return ["th", "st", "nd", "rd"][n % 10] ?? "th";
}

export function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/**
 * z-index croissant pour les overlays (.md-overlay). Toutes les modales partagent
 * le même z-index CSS (9990) ; quand on en ouvre une DEPUIS une autre, le simple
 * ordre DOM ne suffit pas (overlays créés paresseusement puis réutilisés) → une
 * modale enfant pouvait s'afficher SOUS sa mère. On rehausse donc le z-index à
 * l'ouverture pour garantir « la dernière ouverte est au-dessus ».
 */
let topZ = 9990;
export function frontZ(): number {
  topZ += 1;
  return topZ;
}

export function teamName(code: string): string {
  return byCode.get(code)?.nameFr ?? code;
}

export function flagCode(code: string): string {
  return byCode.get(code)?.flagCode ?? "xx";
}

/** Legacy-style flag + trigram. `ph` renders the empty placeholder slot. */
export function flagSpan(code: string | null, options: { name?: boolean } = {}): string {
  if (!code || !byCode.has(code)) return `<span class="ph"></span>`;
  const team = byCode.get(code);
  // `name` → nom complet + un trigramme JUMEAU masqué (.tn-code) : certains
  // contextes (cartes focus sur mobile) basculent en CSS du nom vers le code pour
  // gagner de la place, sans re-render ni détection JS. Masqué partout par défaut.
  const label = options.name
    ? `<span class="tn">${esc(team?.nameFr ?? code)}</span><span class="tn-code" aria-hidden="true">${esc(code)}</span>`
    : `<span class="cd">${esc(code)}</span>`;
  return `<span class="f fi fi-${esc(team?.flagCode ?? "xx")}" data-team="${esc(code)}" title="${esc(team?.nameFr ?? code)}"></span>${label}`;
}

/**
 * Nom de joueur CLIQUABLE (→ fiche joueur) quand on connaît son id API-Football,
 * sinon simple texte échappé. Le contrôleur global `[data-player]` (playerProfile)
 * ouvre la fiche au clic/tap ; pas de tabindex ici (les listes d'évènements sont
 * denses — le joueur reste atteignable au clavier via classements / compositions).
 */
export function playerLink(name: string | null | undefined, id?: number | null): string {
  const n = name ?? "?";
  if (id == null || !Number.isInteger(id)) return esc(n);
  // role/tabindex : atteignable au clavier comme les autres ouvre-fiche (un
  // remplaçant entrant n'apparaît QUE dans le flux d'évènements — WCAG 2.1.1).
  return `<span class="plk" role="button" tabindex="0" data-player="${id}" data-name="${esc(n)}">${esc(n)}</span>`;
}

const ROUND_KEYS = new Set(["group", "r32", "r16", "qf", "sf", "third", "final"]);

/** Nom du tour, traduit (round.group/r32/… ; valeur brute si stage inconnu). */
export function roundName(stage: string): string {
  return ROUND_KEYS.has(stage) ? tr(`round.${stage}`) : stage;
}
